import { useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Bell, Heart, MessageCircle, Star, CheckCheck } from 'lucide-react'
import { notificationsApi } from '../../api/notifications'
import type { AppNotification } from '../../types'

// Where tapping a notification takes you
function targetRoute(n: AppNotification): string {
  switch (n.type) {
    case 'NEW_MESSAGE':
    case 'MATCH_CREATED':
      return '/chat'
    case 'INTEREST_RECEIVED':
    case 'INTEREST_ACCEPTED':
    default:
      return '/interests'
  }
}

function NotifIcon({ type }: { type: AppNotification['type'] }) {
  if (type === 'NEW_MESSAGE') return <MessageCircle size={17} strokeWidth={1.75} className="text-maroon-700" />
  if (type === 'INTEREST_RECEIVED') return <Heart size={17} strokeWidth={1.75} className="text-maroon-700" />
  if (type === 'INTEREST_ACCEPTED') return <Star size={17} strokeWidth={1.75} className="text-pending" />
  return <Bell size={17} strokeWidth={1.75} className="text-maroon-700" />
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export default function NotificationsPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()

  const { data: notifications, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.getAll().then((r) => r.data.data),
  })

  const markAllMut = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
      // Also refresh the nav bell badge, else the unread dot lingers.
      qc.invalidateQueries({ queryKey: ['notif-count'] })
    },
  })

  // Opening the notifications page marks everything seen, so the bell dot clears
  // once the user has looked at them (parity with iOS/Android).
  const markedRef = useRef(false)
  useEffect(() => {
    if (!markedRef.current && notifications?.some((n) => !n.read)) {
      markedRef.current = true
      markAllMut.mutate()
    }
  }, [notifications])

  const markReadMut = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
      qc.invalidateQueries({ queryKey: ['notif-count'] })
    },
  })

  const unreadCount = notifications?.filter((n) => !n.read).length ?? 0

  return (
    <div className="max-w-2xl mx-auto px-4 py-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display font-semibold text-[30px] text-ink-900">Notifications</h1>
          {unreadCount > 0 && (
            <p className="text-xs text-ink-400 font-medium mt-0.5">{unreadCount} unread</p>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllMut.mutate()}
            disabled={markAllMut.isPending}
            className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-maroon-700 hover:underline"
          >
            <CheckCheck size={14} /> Mark all read
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-16 bg-ivory-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : !notifications?.length ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <div className="w-16 h-16 rounded-full bg-ivory-100 border border-gold-500/30 flex items-center justify-center">
            <Bell size={22} strokeWidth={1.5} className="text-gold-600/60" />
          </div>
          <p className="font-display font-semibold text-lg text-ink-900">No notifications yet</p>
          <p className="text-sm text-ink-400">Activity will appear here</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((n) => (
            <div
              key={n.id}
              onClick={() => {
                if (!n.read) markReadMut.mutate(n.id)
                navigate(targetRoute(n))
              }}
              className={`flex items-start gap-3 p-4 rounded-2xl cursor-pointer transition-colors ${
                n.read ? 'bg-ivory-25 border border-ivory-200' : 'bg-ivory-100 border border-ivory-200 border-l-2 border-l-gold-500'
              }`}
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                n.read ? 'bg-ivory-100' : 'bg-ivory-25 shadow-soft'
              }`}>
                <NotifIcon type={n.type} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${n.read ? 'font-medium text-ink-600' : 'font-bold text-ink-900'}`}>
                  {n.title}
                </p>
                {n.body && (
                  <p className="text-xs text-ink-400 mt-0.5 line-clamp-2">{n.body}</p>
                )}
                <p className="text-[11px] text-ink-400/80 mt-1">{timeAgo(n.createdAt)}</p>
              </div>
              {!n.read && (
                <span className="w-2 h-2 rounded-full bg-maroon-700 ring-1 ring-gold-500/60 flex-shrink-0 mt-1.5" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
